import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  QrCode, 
  Camera,
  Barcode,
  Image as ImageIcon, 
  FileText,
  FileCode,
  Volume2,
  Share2, 
  Sparkles, 
  ArrowRight, 
  Search, 
  Cpu, 
  Lock, 
  Zap, 
  Star, 
  Layers, 
  Key,
  Calculator, 
  Video as Youtube, 
  ChevronRight,
  TrendingUp,
  Settings,
  Compass,
  Trophy,
  Crop,
  Activity,
  Pipette,
  LayoutGrid,
  Code,
  Bot,
  Tag,
  Hash,
  BarChart2,
  Gauge,
  Award,
  Globe,
  Calendar,
  Scale,
  DollarSign,
  Clock,
  Wifi,
  Timer,
  Dices
} from 'lucide-react';
import { SEOHead, WebSiteSchema, seoData } from '../seo';
import { FAQSchema, BreadcrumbSchema } from '../seo/StructuredData';
import { AdUnit } from './AdUnit';

export const Home = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const location = useLocation();

  // Sync state with url query parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const searchVal = queryParams.get('search') || '';
    const categoryVal = queryParams.get('category') || 'All';
    setSearchQuery(searchVal);
    setActiveCategory(categoryVal);
  }, [location.search]);

  // Static list of all tools in the Toolmetrico bundle
  const tools = [
    {
      id: 'qrcode-generator',
      title: 'QR Code Generator',
      description: 'Design beautiful, customized QR codes with gradients, custom dots, frames, and custom corporate branding logos. High resolution outputs.',
      category: 'QR & Generator Tools',
      icon: QrCode,
      status: 'active', // active, coming_soon
      path: '/qrcode-generator',
      badge: 'Flagship Tool',
      color: 'var(--primary)',
      bgLight: 'rgba(5, 117, 251, 0.05)'
    },
    {
      id: 'qr-scanner',
      title: 'QR Code Scanner',
      description: 'Scan QR codes instantly in real-time using your webcam or mobile camera, or drop any image file. Secure client-side decryption.',
      category: 'QR & Generator Tools',
      icon: Camera,
      status: 'active',
      path: '/qr-scanner',
      badge: 'Secure Parse',
      color: '#0ea5e9',
      bgLight: 'rgba(14, 165, 233, 0.05)'
    },
    {
      id: 'barcode-generator',
      title: 'Barcode Generator',
      description: 'Create high-resolution vector Code 39 barcodes for product labels, packaging tags, inventory control, and asset track markings.',
      category: 'QR & Generator Tools',
      icon: Barcode,
      status: 'active',
      path: '/barcode-generator',
      badge: 'Alphanumeric',
      color: '#4f46e5',
      bgLight: 'rgba(79, 70, 229, 0.05)'
    },
    {
      id: 'custom-generators',
      title: 'Sleek Key & Password Creator',
      description: 'Generate hyper-secure passwords, cryptographic hashes, random tokens, and UUID keys client-side with adjustable complexity parameters.',
      category: 'QR & Generator Tools',
      icon: Key,
      status: 'active',
      path: '/key-generator',
      badge: 'Safe & Secure',
      color: '#a855f7',
      bgLight: 'rgba(168, 85, 247, 0.05)'
    },
    {
      id: 'image-compressor',
      title: 'Smart Image Compressor',
      description: 'Shrink WebP, PNG, JPG and GIF image file sizes by up to 90% without losing visual quality. Runs 100% locally on your computer.',
      category: 'Image Tools',
      icon: ImageIcon,
      status: 'active',
      path: '/image-compressor',
      badge: 'Instant Scale',
      color: 'var(--secondary)',
      bgLight: 'rgba(14, 165, 233, 0.05)'
    },
    {
      id: 'remove-background',
      title: 'Remove Image Background',
      description: 'Instantly remove solid white, black, or customized background colors from your product shots, graphics and transparent logos.',
      category: 'Image Tools',
      icon: Layers,
      status: 'active',
      path: '/remove-background',
      badge: 'Pixel Keyer',
      color: '#059669',
      bgLight: 'rgba(5, 150, 105, 0.05)'
    },
    {
      id: 'image-workshop',
      title: 'Image Resizer & Cropper',
      description: 'Resize image dimensions with ratio locks, choose file formats, or visually crop layouts with custom canvas overlay presets.',
      category: 'Image Tools',
      icon: Crop,
      status: 'active',
      path: '/image-workshop',
      badge: 'Interactive',
      color: '#14b8a6',
      bgLight: 'rgba(20, 184, 166, 0.05)'
    },
    {
      id: 'gradient-generator',
      title: 'CSS Gradient Generator',
      description: 'Design breathtaking linear or radial CSS gradients, sample presets, copy Tailwind utility values, and download 4K wallpapers.',
      category: 'Image Tools',
      icon: Compass,
      status: 'active',
      path: '/gradient-generator',
      badge: 'Designer Suite',
      color: '#06b6d4',
      bgLight: 'rgba(6, 182, 212, 0.05)'
    },
    {
      id: 'format-converter',
      title: 'Multi-Format Asset Converter',
      description: 'Convert images between PNG, JPG, WEBP, SVG, BMP, AVIF, HEIC, TIFF, PDF and Base64 format layers securely with zero server uploads.',
      category: 'Image Tools',
      icon: Settings,
      status: 'active',
      path: '/format-converter',
      badge: 'Pro Suite',
      color: '#06b6d4',
      bgLight: 'rgba(6, 182, 212, 0.05)'
    },
    {
      id: 'image-color-picker',
      title: 'Image Color Picker',
      description: 'Extract exact HEX, RGB, and HSL colors from any uploaded image with a live zoom magnifier lens and build custom downloadable palettes.',
      category: 'Image Tools',
      icon: Pipette,
      status: 'active',
      path: '/image-color-picker',
      badge: 'Precise Lens',
      color: '#3b82f6',
      bgLight: 'rgba(59, 130, 246, 0.05)'
    },
    {
      id: 'photo-collage',
      title: 'Photo Collage Maker',
      description: 'Combine multiple photos into beautiful, customizable grid layouts and creative canvases. Adjust gaps, borders, and colors client-side.',
      category: 'Image Tools',
      icon: LayoutGrid,
      status: 'active',
      path: '/photo-collage',
      badge: 'Grid Studio',
      color: '#ec4899',
      bgLight: 'rgba(236, 72, 153, 0.05)'
    },
    {
      id: 'pdf-utilities',
      title: 'Ultimate PDF Power Suite',
      description: 'Combine, split, compress, protect, decrypt, and convert PDFs to Word, JPG, Excel, PPT, and HTML securely with zero server uploads.',
      category: 'PDF Tools',
      icon: FileText,
      status: 'active',
      path: '/pdf-utilities',
      badge: 'Secure Core',
      color: '#ef4444',
      bgLight: 'rgba(239, 68, 68, 0.05)'
    },
    {
      id: 'youtube-video-downloader',
      title: 'YouTube Video Downloader',
      description: 'Download high-quality YouTube MP4 videos (1080p Full HD, 720p) and crystal-clear MP3 audio streams instantly without limits.',
      category: 'YouTube Tools',
      icon: Youtube,
      status: 'active',
      path: '/youtube-video-downloader',
      badge: 'MP4 & MP3',
      color: '#dc2626',
      bgLight: 'rgba(220, 38, 38, 0.05)'
    },
    {
      id: 'youtube-thumbnail-downloader',
      title: 'YouTube Thumbnail Downloader',
      description: 'Extract and download high-resolution cover thumbnail images from any YouTube video in multiple sizes and crisp resolutions.',
      category: 'YouTube Tools',
      icon: ImageIcon,
      status: 'active',
      path: '/youtube-downloader',
      badge: 'HD Cover Kit',
      color: '#ef4444',
      bgLight: 'rgba(239, 68, 68, 0.05)'
    },
    {
      id: 'youtube-title-generator',
      title: 'YouTube Title Generator',
      description: 'Generate catchy, high-CTR titles using psychology triggers and real-time mobile feed character truncation length analysis.',
      category: 'YouTube Tools',
      icon: Trophy,
      status: 'active',
      path: '/youtube-title-generator',
      badge: 'Viral Hook',
      color: '#ef4444',
      bgLight: 'rgba(239, 68, 68, 0.05)'
    },
    {
      id: 'social-engagement',
      title: 'Instagram & TikTok Engagement Engine',
      description: 'Generate optimized bio structures, discover trending hashtags, calculate post metrics, and boost organic social media click rates.',
      category: 'YouTube Tools',
      icon: Share2,
      status: 'coming_soon',
      badge: 'Coming Soon',
      color: '#0284c7',
      bgLight: 'rgba(2, 132, 199, 0.05)'
    },
    {
      id: 'audio-workshop',
      title: 'Audio Processing Workshop',
      description: 'Natively cut audio bounds, compress signal peaks, boost bass gain DB, alter speed tempo, or convert file wrappers client-side.',
      category: 'Audio Tools',
      icon: Activity,
      status: 'active',
      path: '/audio-workshop',
      badge: 'Waveform DSP',
      color: '#a855f7',
      bgLight: 'rgba(168, 85, 247, 0.05)'
    },
    {
      id: 'code-minifier',
      title: 'Code Compressor & Minifier',
      description: 'Minify HTML templates, collapse CSS style sheets, and strip JS structures client-side to increase load speed and search ranks.',
      category: 'Developer Tools',
      icon: FileCode,
      status: 'active',
      path: '/code-minifier',
      badge: 'SEO Vital',
      color: 'var(--primary)',
      bgLight: 'rgba(5, 117, 251, 0.05)'
    },
    {
      id: 'text-to-speech',
      title: 'AI Speech Synthesis Studio',
      description: 'Convert scripts into high fidelity vocal audio soundwaves, custom select vocalist accents, rate speed factors and pitch offsets.',
      category: 'Audio Tools',
      icon: Volume2,
      status: 'active',
      path: '/text-to-speech',
      badge: 'AI Vocalist',
      color: '#f43f5e',
      bgLight: 'rgba(244, 63, 94, 0.05)'
    },
    {
      id: 'loan-calculator',
      title: 'Loan & Mortgage Planner',
      description: 'Calculate monthly loan schedules, evaluate interest APR distributions, and estimate terms savings with extra payments.',
      category: 'Financial Tools',
      icon: Calculator,
      status: 'active',
      path: '/loan-calculator',
      badge: 'Pro Finance',
      color: '#059669',
      bgLight: 'rgba(5, 150, 105, 0.05)'
    },
    {
      id: 'meta-tag-generator',
      title: 'Meta Tag Generator',
      description: 'Create perfect HTML meta tags for Google search engine indexing. Custom titles, descriptions, and crawling directives with live SERP preview.',
      category: 'SEO & Marketing Tools',
      icon: Code,
      status: 'active',
      path: '/meta-tag-generator',
      badge: 'SERP Live',
      color: '#10b981',
      bgLight: 'rgba(16, 185, 129, 0.05)'
    },
    {
      id: 'open-graph-generator',
      title: 'Open Graph Generator',
      description: 'Generate Open Graph and Twitter Card protocol tags. Preview social share snippets for Facebook, LinkedIn, Twitter, and Discord instantly.',
      category: 'SEO & Marketing Tools',
      icon: Share2,
      status: 'active',
      path: '/open-graph-generator',
      badge: 'Social Meta',
      color: '#0284c7',
      bgLight: 'rgba(2, 132, 199, 0.05)'
    },
    {
      id: 'robots-txt-generator',
      title: 'Robots.txt Generator',
      description: 'Create customized robots.txt files instantly. Define specific user-agent directives, crawl delays, and sitemap paths for search engine spiders.',
      category: 'SEO & Marketing Tools',
      icon: Bot,
      status: 'active',
      path: '/robots-txt-generator',
      badge: 'Spiders',
      color: '#10b981',
      bgLight: 'rgba(16, 185, 129, 0.05)'
    },
    {
      id: 'sitemap-generator',
      title: 'XML Sitemap Generator',
      description: 'Generate valid XML sitemaps for Google Search Console. Configure change frequencies, priority scoring, and last modified dates instantly.',
      category: 'SEO & Marketing Tools',
      icon: FileCode,
      status: 'active',
      path: '/sitemap-generator',
      badge: 'XML Roadmap',
      color: '#f59e0b',
      bgLight: 'rgba(245, 158, 11, 0.05)'
    },
    {
      id: 'youtube-tag-generator',
      title: 'YouTube Tag Generator',
      description: 'Generate high-volume, trending YouTube tags and long-tail keywords. Boost video rankings and maximize reach with our viral tag engine.',
      category: 'YouTube Tools',
      icon: Tag,
      status: 'active',
      path: '/youtube-tag-generator',
      badge: 'AI Keywords',
      color: '#ef4444',
      bgLight: 'rgba(239, 68, 68, 0.05)'
    },
    {
      id: 'hashtag-generator',
      title: 'Viral Hashtag Generator',
      description: 'Generate trending, niche, and high-engagement hashtags for Instagram, TikTok, LinkedIn, and YouTube. Boost organic social reach.',
      category: 'SEO & Marketing Tools',
      icon: Hash,
      status: 'active',
      path: '/hashtag-generator',
      badge: 'Viral Reach',
      color: '#0284c7',
      bgLight: 'rgba(2, 132, 199, 0.05)'
    },
    {
      id: 'keyword-density-checker',
      title: 'Keyword Density Checker',
      description: 'Analyze article keyword density percentages, word count, and n-gram repetitions. Optimize content for SEO without keyword stuffing risks.',
      category: 'SEO & Marketing Tools',
      icon: BarChart2,
      status: 'active',
      path: '/keyword-density-checker',
      badge: 'Stuffing Guard',
      color: '#10b981',
      bgLight: 'rgba(16, 185, 129, 0.05)'
    },
    {
      id: 'seo-analyzer',
      title: 'SEO Analyzer & Audit Score',
      description: 'Audit your website content, headings, meta descriptions, and image alt tags instantly. Get an actionable SEO optimization score.',
      category: 'SEO & Marketing Tools',
      icon: Gauge,
      status: 'active',
      path: '/seo-analyzer',
      badge: 'Page Audit',
      color: '#10b981',
      bgLight: 'rgba(16, 185, 129, 0.05)'
    },
    {
      id: 'backlink-checker',
      title: 'Backlink Checker & Profiler',
      description: 'Simulate domain authority, trust flow, referring domains, and top anchor texts instantly. Comprehensive client-side backlink audit tool.',
      category: 'SEO & Marketing Tools',
      icon: Award,
      status: 'active',
      path: '/backlink-checker',
      badge: 'Link Authority',
      color: '#0284c7',
      bgLight: 'rgba(2, 132, 199, 0.05)'
    },
    {
      id: 'google-index-checker',
      title: 'Google Index Checker',
      description: 'Verify URL indexing status with instant Google search operators. Bulk check webpage search console discoverability client-side.',
      category: 'SEO & Marketing Tools',
      icon: Globe,
      status: 'active',
      path: '/google-index-checker',
      badge: 'SERP Check',
      color: '#10b981',
      bgLight: 'rgba(16, 185, 129, 0.05)'
    },
    {
      id: 'age-calculator',
      title: 'Exact Age Calculator',
      description: 'Calculate your exact age in years, months, days, hours, and minutes. Check upcoming birthday countdowns and day of birth instantly.',
      category: 'Utility Tools',
      icon: Calendar,
      status: 'active',
      path: '/age-calculator',
      badge: 'Exact Age',
      color: '#10b981',
      bgLight: 'rgba(16, 185, 129, 0.05)'
    },
    {
      id: 'bmi-calculator',
      title: 'BMI & BMR Calculator',
      description: 'Calculate your Body Mass Index (BMI) instantly. Discover your healthy weight range, weight category, and BMR estimate online.',
      category: 'Utility Tools',
      icon: Activity,
      status: 'active',
      path: '/bmi-calculator',
      badge: 'Health',
      color: '#0284c7',
      bgLight: 'rgba(2, 132, 199, 0.05)'
    },
    {
      id: 'currency-converter',
      title: 'Live Currency Converter',
      description: 'Convert between USD, EUR, GBP, JPY, CAD, AUD, INR, and global currencies instantly. Check live simulated foreign exchange rate trends.',
      category: 'Utility Tools',
      icon: DollarSign,
      status: 'active',
      path: '/currency-converter',
      badge: 'Forex Live',
      color: '#f59e0b',
      bgLight: 'rgba(245, 158, 11, 0.05)'
    },
    {
      id: 'unit-converter',
      title: 'Universal Unit Converter',
      description: 'Convert length, weight, temperature, area, volume, and speed units instantly. Free universal conversion calculator with formula guides.',
      category: 'Utility Tools',
      icon: Scale,
      status: 'active',
      path: '/unit-converter',
      badge: 'Metric',
      color: '#8b5cf6',
      bgLight: 'rgba(139, 92, 246, 0.05)'
    },
    {
      id: 'time-zone-converter',
      title: 'Time Zone Converter',
      description: 'Convert local time across New York, London, Tokyo, Sydney, Paris, and global timezones. Plan international meetings seamlessly.',
      category: 'Utility Tools',
      icon: Clock,
      status: 'active',
      path: '/time-zone-converter',
      badge: 'Meetings',
      color: '#ec4899',
      bgLight: 'rgba(236, 72, 153, 0.05)'
    },
    {
      id: 'internet-speed-test',
      title: 'Internet Speed Test',
      description: 'Test your broadband internet download and upload speeds in real-time. Check network ping latency and connection stability instantly.',
      category: 'Utility Tools',
      icon: Wifi,
      status: 'active',
      path: '/internet-speed-test',
      badge: 'Bandwidth',
      color: '#10b981',
      bgLight: 'rgba(16, 185, 129, 0.05)'
    },
    {
      id: 'stopwatch',
      title: 'Precision Stopwatch',
      description: 'Track time with millisecond precision. Start, pause, reset, and log split lap times instantly. Perfect for workouts and studying.',
      category: 'Utility Tools',
      icon: Clock,
      status: 'active',
      path: '/stopwatch',
      badge: 'Precision',
      color: '#ef4444',
      bgLight: 'rgba(239, 68, 68, 0.05)'
    },
    {
      id: 'countdown-timer',
      title: 'Countdown Timer & Alarm',
      description: 'Set custom countdown timers for study sessions, cooking, or productivity. Features visual progress rings and alarm audio notifications.',
      category: 'Utility Tools',
      icon: Timer,
      status: 'active',
      path: '/countdown-timer',
      badge: 'Pomodoro',
      color: '#f59e0b',
      bgLight: 'rgba(245, 158, 11, 0.05)'
    },
    {
      id: 'random-picker-wheel',
      title: 'Random Picker Wheel',
      description: 'Input names, choices, or giveaway contestants and spin the random picker wheel. Fun, interactive decision maker with confetti celebration.',
      category: 'Utility Tools',
      icon: Dices,
      status: 'active',
      path: '/random-picker-wheel',
      badge: 'Spin Wheel',
      color: '#06b6d4',
      bgLight: 'rgba(6, 182, 212, 0.05)'
    },
    {
      id: 'calendar-generator',
      title: 'Calendar Generator',
      description: 'Generate customized monthly and yearly calendars. Highlight notes, track holidays, and export clean printable grid layouts instantly.',
      category: 'Utility Tools',
      icon: Calendar,
      status: 'active',
      path: '/calendar-generator',
      badge: 'Printable',
      color: '#10b981',
      bgLight: 'rgba(16, 185, 129, 0.05)'
    }
  ];

  // Filters categories based on clicked selection
  const categoriesList = ['All', 'QR & Generator Tools', 'Image Tools', 'PDF Tools', 'YouTube Tools', 'Audio Tools', 'Developer Tools', 'Financial Tools', 'SEO & Marketing Tools', 'Utility Tools'];

  // Match filters with tool cards
  const filteredTools = tools.filter((t) => {
    const matchesSearch = t.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          t.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          t.category.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = activeCategory === 'All' || t.category === activeCategory;
    
    return matchesSearch && matchesCategory;
  });

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%', paddingBottom: '40px' }}>
      <SEOHead 
        title={seoData.home.title}
        description={seoData.home.description}
        keywords={seoData.home.keywords}
        path="/"
      />
      
      {/* Big Search Input Field Bar (Floating) */}
      <div style={{
        position: 'relative',
        maxWidth: '680px',
        margin: '0 auto 10px auto',
        width: '100%'
      }}>
        <input 
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search across all modules (e.g. QR, AVIF, PDF, YouTube)..."
          style={{
            width: '100%',
            height: '56px',
            padding: '0 20px 0 52px',
            fontSize: '1.05rem',
            borderRadius: '99px',
            border: '1.5px solid var(--border-color)',
            background: '#ffffff',
            color: 'var(--text-primary)',
            outline: 'none',
            boxSizing: 'border-box',
            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)',
            transition: 'var(--transition-smooth)'
          }}
          className="home-search-input"
        />
        <Search 
          size={20} 
          style={{
            position: 'absolute',
            left: '18px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--text-muted)'
          }}
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            style={{
              position: 'absolute',
              right: '18px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(15, 23, 42, 0.05)',
              border: 'none',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'var(--text-secondary)'
            }}
          >
            &times;
          </button>
        )}
      </div>

      {/* Directory Content Title Bar */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '20px',
        marginBottom: '28px',
        borderBottom: '1px solid var(--border-color)',
        paddingBottom: '16px'
      }}>
        {/* Dynamic Category Selector Pills */}
        <div style={{
          display: 'flex',
          gap: '8px',
          overflowX: 'auto',
          whiteSpace: 'nowrap',
          maxWidth: '100%'
        }}>
          {categoriesList.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              style={{
                background: activeCategory === cat ? 'var(--primary)' : 'rgba(15, 23, 42, 0.03)',
                color: activeCategory === cat ? '#ffffff' : 'var(--text-secondary)',
                border: 'none',
                fontWeight: 700,
                fontSize: '0.85rem',
                padding: '10px 18px',
                borderRadius: '99px',
                cursor: 'pointer',
                transition: 'var(--transition-smooth)'
              }}
              className="category-pill-button"
            >
              {cat === 'All' ? 'All Workspace Tools' : cat}
            </button>
          ))}
        </div>

        {/* Counter Results Label */}
        <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-muted)' }}>
          Showing {filteredTools.length} of {tools.length} products
        </div>
      </div>

      {/* Cards Matrix Grid Layout */}
      {filteredTools.length > 0 ? (
        <div 
          className="home-tools-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '24px'
          }}
        >
          {filteredTools.map((t, index) => {
            const CardIcon = t.icon;
            const isActive = t.status === 'active';
            
            return (
              <React.Fragment key={t.id}>
                {(index === 3 || (index === 9 && filteredTools.length > 9) || (index === 15 && filteredTools.length > 15)) && (
                  <div style={{ gridColumn: '1 / -1', width: '100%', margin: '8px 0' }} className="desktop-inline-ad">
                    <AdUnit />
                  </div>
                )}
                <div
                  style={{
                    background: '#ffffff',
                    border: isActive ? '1.5px solid rgba(5, 117, 251, 0.15)' : '1px solid var(--border-color)',
                    borderRadius: '20px',
                    padding: '28px',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px',
                    boxShadow: isActive ? '0 10px 30px -15px rgba(5, 117, 251, 0.1)' : 'var(--shadow-sm)',
                    transition: 'var(--transition-smooth)'
                  }}
                className={`tool-card-wrapper ${isActive ? 'active-tool' : 'inactive-tool'}`}
              >
                {/* Upper row: icon & status badge */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{
                    background: t.bgLight,
                    color: t.color,
                    padding: '12px',
                    borderRadius: '14px',
                    display: 'inline-flex'
                  }}>
                    <CardIcon size={24} />
                  </div>
                  
                  {/* Status marker */}
                  <span 
                    className="featured-badge-tag"
                    style={{
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      padding: '4px 10px',
                      borderRadius: '99px',
                      color: isActive ? '#059669' : 'var(--text-muted)',
                      background: isActive ? 'rgba(5, 150, 105, 0.08)' : 'rgba(15, 23, 42, 0.04)',
                      border: isActive ? '1px solid rgba(5, 150, 105, 0.15)' : '1px solid transparent'
                    }}
                  >
                    {t.badge}
                  </span>
                </div>

                {/* Body metadata */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flexGrow: 1 }}>
                  <h3 
                    className="tool-title-header"
                    style={{
                      fontSize: '1.2rem',
                      fontWeight: 800,
                      color: 'var(--text-primary)',
                      margin: 0,
                      letterSpacing: '-0.01em',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    {t.title}
                    {isActive && <Star size={14} style={{ fill: '#eab308', color: '#eab308' }} />}
                  </h3>
                  <p 
                    className="tool-desc-para"
                    style={{
                      fontSize: '0.88rem',
                      color: 'var(--text-secondary)',
                      lineHeight: 1.55,
                      margin: 0
                    }}
                  >
                    {t.description}
                  </p>
                </div>

                {/* Call to action button */}
                <div>
                  {isActive ? (
                    <Link
                      to={t.path}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        textDecoration: 'none',
                        background: 'var(--primary)',
                        color: '#ffffff',
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        padding: '12px',
                        borderRadius: '10px',
                        transition: 'var(--transition-smooth)',
                        boxShadow: '0 4px 12px -2px rgba(5, 117, 251, 0.2)'
                      }}
                      className="card-launch-button"
                    >
                      Launch <ArrowRight size={14} />
                    </Link>
                  ) : (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      background: 'rgba(15, 23, 42, 0.03)',
                      color: 'var(--text-muted)',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      padding: '12px',
                      borderRadius: '10px',
                      border: '1px dashed var(--border-color)',
                      cursor: 'not-allowed'
                    }}>
                      System Under Assembly
                    </div>
                  )}
                </div>
              </div>
            </React.Fragment>
            );
          })}
        </div>
      ) : (
        <div style={{
          textAlign: 'center',
          padding: '60px 24px',
          background: '#ffffff',
          border: '1px solid var(--border-color)',
          borderRadius: '20px',
          maxWidth: '480px',
          margin: '0 auto'
        }}>
          <p style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '8px' }}>No tools matched search</p>
          <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', margin: '0 0 20px 0' }}>We currently do not have a tool matching "{searchQuery}". Try searching for "QR" or "PDF".</p>
          <button
            onClick={() => { setSearchQuery(''); setActiveCategory('All'); }}
            style={{
              background: 'var(--primary)',
              color: '#ffffff',
              border: 'none',
              fontWeight: 700,
              fontSize: '0.85rem',
              padding: '10px 16px',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Clear Filters & Reload
          </button>
        </div>
      )}

      {/* Styled system parameters embed */}
      <style>{`
        .home-search-input:focus {
          border-color: var(--primary) !important;
          box-shadow: 0 10px 30px -10px rgba(5, 117, 251, 0.15), 0 0 0 3px rgba(5, 117, 251, 0.1) !important;
        }

        .category-pill-button:hover {
          background: var(--primary) !important;
          color: #ffffff !important;
        }

        .tool-card-wrapper {
          transition: transform 0.25s, box-shadow 0.25s, border-color 0.25s !important;
        }

        .active-tool:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 35px -15px rgba(5, 117, 251, 0.18), 0 0 0 1px rgba(5, 117, 251, 0.05) !important;
          border-color: var(--primary) !important;
        }

        .inactive-tool:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-md) !important;
        }

        .card-launch-button:hover {
          background: var(--primary-hover) !important;
          transform: translateY(-1px);
          box-shadow: 0 6px 16px -2px rgba(5, 117, 251, 0.35) !important;
        }
      `}</style>

      {/* Render AdUnit below grid if fewer than 3 tools are filtered */}
      {filteredTools.length <= 3 && filteredTools.length > 0 && (
        <div style={{ marginTop: '32px' }}>
          <AdUnit />
        </div>
      )}

      {/* SEO Content Section - Helps with indexing and AdSense approval */}
      <section aria-labelledby="about-toolmetrico" style={{
        marginTop: '64px',
        padding: '40px',
        background: '#ffffff',
        borderRadius: '20px',
        border: '1px solid var(--border-color)',
        textAlign: 'center'
      }}>
        <h1 id="about-toolmetrico" style={{
          fontSize: '1.8rem',
          fontWeight: 800,
          color: 'var(--text-primary)',
          marginBottom: '16px',
          lineHeight: 1.3,
          textAlign: 'center'
        }}>
          Free Online Tools for Developers, Designers & Creators
        </h1>
        <p style={{
          fontSize: '1rem',
          color: 'var(--text-secondary)',
          lineHeight: 1.7,
          marginBottom: '24px',
          maxWidth: '800px',
          margin: '0 auto 24px auto',
          textAlign: 'center'
        }}>
          Toolmetrico is a comprehensive suite of free online tools designed to boost your productivity. 
          From generating custom QR codes and compressing images to merging PDFs and creating secure passwords, 
          all our tools run 100% in your browser for maximum speed and privacy. No file uploads, no data collection, 
          no registration required.
        </p>

        <h2 style={{
          fontSize: '1.25rem',
          fontWeight: 700,
          color: 'var(--text-primary)',
          marginBottom: '16px',
          marginTop: '32px',
          textAlign: 'center'
        }}>
          Why Choose Toolmetrico?
        </h2>
        <ul style={{
          fontSize: '0.95rem',
          color: 'var(--text-secondary)',
          lineHeight: 1.8,
          listStyleType: 'none',
          padding: 0,
          margin: '0 auto 32px auto',
          maxWidth: '720px',
          textAlign: 'left',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <span style={{ color: 'var(--primary)', fontWeight: 'bold', fontSize: '1.2rem', lineHeight: '1.4rem', userSelect: 'none' }}>•</span>
            <div><strong>100% Client-Side Processing</strong> — Your files never leave your device. All operations happen locally in your browser.</div>
          </li>
          <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <span style={{ color: 'var(--primary)', fontWeight: 'bold', fontSize: '1.2rem', lineHeight: '1.4rem', userSelect: 'none' }}>•</span>
            <div><strong>No Registration Required</strong> — Use any tool instantly without creating an account or providing personal information.</div>
          </li>
          <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <span style={{ color: 'var(--primary)', fontWeight: 'bold', fontSize: '1.2rem', lineHeight: '1.4rem', userSelect: 'none' }}>•</span>
            <div><strong>Fast & Lightweight</strong> — Optimized for speed with minimal load times and instant results.</div>
          </li>
          <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <span style={{ color: 'var(--primary)', fontWeight: 'bold', fontSize: '1.2rem', lineHeight: '1.4rem', userSelect: 'none' }}>•</span>
            <div><strong>Mobile Friendly</strong> — All tools work seamlessly on desktop, tablet, and mobile devices.</div>
          </li>
          <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <span style={{ color: 'var(--primary)', fontWeight: 'bold', fontSize: '1.2rem', lineHeight: '1.4rem', userSelect: 'none' }}>•</span>
            <div><strong>Completely Free</strong> — No hidden fees, no premium tiers, no usage limits.</div>
          </li>
        </ul>

        <h2 style={{
          fontSize: '1.25rem',
          fontWeight: 700,
          color: 'var(--text-primary)',
          marginBottom: '16px',
          marginTop: '32px',
          textAlign: 'center'
        }}>
          Popular Tools
        </h2>
        <p style={{
          fontSize: '0.95rem',
          color: 'var(--text-secondary)',
          lineHeight: 1.7,
          maxWidth: '800px',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          Our most popular tools include the <Link to="/qrcode-generator" style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: 600 }}>QR Code Generator</Link> for creating custom branded QR codes, 
          the <Link to="/image-compressor" style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: 600 }}>Image Compressor</Link> for reducing file sizes without quality loss, 
          the <Link to="/pdf-utilities" style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: 600 }}>PDF Tools Suite</Link> for merging, splitting, and converting documents, 
          and the <Link to="/format-converter" style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: 600 }}>Format Converter</Link> for converting between image formats like PNG, JPG, WebP, and AVIF.
        </p>
      </section>
    </div>
  );
};

const badgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  background: 'rgba(15, 23, 42, 0.03)',
  padding: '6px 14px',
  borderRadius: '8px',
  border: '1px solid rgba(15, 23, 42, 0.03)'
};

export default Home;
